import {
   Box, Table, TableBody, TableCell, TableContainer,
   TableHead, TablePagination, TableRow, Typography,
   InputAdornment, TextField, Tooltip, TableSortLabel,
   Tabs, Tab, Button,
   ToggleButtonGroup, ToggleButton,
} from '@mui/material'
import React, { useCallback, useEffect, useState } from 'react'
import SearchIcon from '@mui/icons-material/Search'
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet'
import AllInclusiveIcon from '@mui/icons-material/AllInclusive'
import AccountBalanceIcon from '@mui/icons-material/AccountBalance'
import SyncAltIcon from '@mui/icons-material/SyncAlt'
import VisibilityIcon from '@mui/icons-material/Visibility'
import FileDownloadIcon from '@mui/icons-material/FileDownload'
import AttachMoneyIcon from '@mui/icons-material/AttachMoney'
import { useNavigate } from 'react-router-dom'
import { ApiGetPlatformBalances, ApiExportPlatformBalances } from '../../apiService/Core/Apicall'
import { usePersistedState } from '../../hooks/userPersistedState'


interface Wallets {
   borrow: number
   collateral: number
   lending: number
   trading: number
   staking: number
}


export interface PlatformAsset {
   currency: string
   provider: string
   currencyType: string
   balance: number
   available: number
   held: number
   valueUsd?: number
   wallets: Wallets | null
}


type SortKey = 'currency' | 'balance' | 'available' | 'held'
type SortDir = 'asc' | 'desc'
type ProviderFilter = 'all' | 'sfox' | 'c2c'
type HoldingsFilter = 'all' | 'with_balance'


export const CURRENCY_META: Record<string, { color: string; bg: string; border: string; label: string }> = {
   btc: { color: '#f59e0b', bg: '#f59e0b15', border: '#f59e0b40', label: 'Bitcoin' },
   eth: { color: '#a78bfa', bg: '#a78bfa15', border: '#a78bfa40', label: 'Ethereum' },
   sol: { color: '#10b981', bg: '#10b98115', border: '#10b98140', label: 'Solana' },
   doge: { color: '#f59e0b', bg: '#f59e0b10', border: '#f59e0b30', label: 'Dogecoin' },
   xrp: { color: '#3b82f6', bg: '#3b82f615', border: '#3b82f640', label: 'XRP' },
   usdt: { color: '#10b981', bg: '#10b98110', border: '#10b98130', label: 'Tether' },
   usdc: { color: '#3b82f6', bg: '#3b82f610', border: '#3b82f630', label: 'USD Coin' },
   usd: { color: '#22d3ee', bg: '#22d3ee10', border: '#22d3ee30', label: 'USD' },
   ltc: { color: '#9ca3af', bg: '#9ca3af10', border: '#9ca3af30', label: 'Litecoin' },
   link: { color: '#3b82f6', bg: '#3b82f610', border: '#3b82f630', label: 'Chainlink' },
   shib: { color: '#ef4444', bg: '#ef444410', border: '#ef444430', label: 'Shiba Inu' },
   pepe: { color: '#10b981', bg: '#10b98110', border: '#10b98130', label: 'Pepe' },
   bch: { color: '#10b981', bg: '#10b98110', border: '#10b98130', label: 'Bitcoin Cash' },
   ena: { color: '#ec4899', bg: '#ec489910', border: '#ec489930', label: 'Ethena' },
   sky: { color: '#6366f1', bg: '#6366f110', border: '#6366f130', label: 'Sky' },
   algo: { color: '#3b82f6', bg: '#3b82f610', border: '#3b82f630', label: 'Algorand' },
   asi: { color: '#a78bfa', bg: '#a78bfa10', border: '#a78bfa30', label: 'ASI' },
   aud: { color: '#10b981', bg: '#10b98110', border: '#10b98130', label: 'AUD' },
}


export const getMeta = (currency: string) =>
   CURRENCY_META[currency.toLowerCase()] ?? { color: '#9a9a9a', bg: '#9a9a9a10', border: '#9a9a9a30', label: currency.toUpperCase() }


export const fmt = (n: number, decimals = 4) => {
   if (Math.abs(n) >= 1e6) return (n / 1e6).toFixed(2) + 'M'
   if (Math.abs(n) >= 1e3) return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
   return n?.toLocaleString('en-US', { minimumFractionDigits: decimals > 4 ? 4 : decimals, maximumFractionDigits: decimals > 4 ? 8 : decimals })
}


export const fmtUsd = (n: number) =>
   n?.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 })


export const CurrencyBadge: React.FC<{ currency: string }> = ({ currency }) => {
   const m = getMeta(currency)
   return (
       <Box sx={{
           display: 'inline-flex', alignItems: 'center', gap: 0.8,
           px: 1.5, py: 0.5, borderRadius: '6px',
           background: m.bg, border: `1px solid ${m.border}`,
           fontSize: 12, fontWeight: 700, color: m.color,
           letterSpacing: '0.05em', textTransform: 'uppercase',
           cursor: 'default',
       }}>
           <Box sx={{ width: 6, height: 6, borderRadius: '50%', background: m.color, flexShrink: 0 }} />
           {currency.toUpperCase()}
       </Box>
   )
}


const SummaryStatCard: React.FC<{ label: string; value: string; sub?: string; color: string; icon: React.ReactNode }> = ({ label, value, sub, color, icon }) => (
   <Box sx={{
       background: 'linear-gradient(135deg, #2a2a2a 0%, #222 100%)',
       border: '1px solid #333',
       borderRadius: '14px',
       p: 2.5,
       position: 'relative',
       overflow: 'hidden',
       transition: 'border-color 0.25s',
       '&:hover': { borderColor: color + '60' },
       '&::before': {
           content: '""',
           position: 'absolute',
           top: 0, left: 0, right: 0,
           height: '2px',
           background: `linear-gradient(90deg, ${color}00, ${color}, ${color}00)`,
       },
   }}>
       <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
           <Typography fontSize={10} color="#6f6f6f" textTransform="uppercase" letterSpacing="0.1em">{label}</Typography>
           <Box sx={{ color, opacity: 0.7 }}>{icon}</Box>
       </Box>
       <Typography fontSize={20} fontWeight={800} color={color} sx={{ letterSpacing: '-0.02em' }}>{value}</Typography>
       {sub && <Typography fontSize={10} color="#5a5a5a" mt={0.5}>{sub}</Typography>}
   </Box>
)


interface HeadCol {
   id: string
   label: string
   sortKey?: SortKey
}


const PlatformBalance: React.FC = () => {
   const navigate = useNavigate()


   const [assets, setAssets] = useState<PlatformAsset[]>([])
   const [search, setSearch] = usePersistedState('platformBalance:search', '')
   const [debouncedSearch, setDebouncedSearch] = useState(search)
   const [total, setTotal] = useState(0)
   const [page, setPage] = usePersistedState('platformBalance:page', 0)
   const [rowsPerPage, setRowsPerPage] = usePersistedState('platformBalance:rowsPerPage', 10)
   const [sortKey, setSortKey] = usePersistedState<SortKey>('platformBalance:sortKey', 'currency')
   const [sortDir, setSortDir] = usePersistedState<SortDir>('platformBalance:sortDir', 'asc')
   const [provider, setProvider] = usePersistedState<ProviderFilter>('platformBalance:provider', 'all')
   const [holdingsFilter, setHoldingsFilter] = usePersistedState<HoldingsFilter>('platformBalance:holdingsFilter', 'all')
   const [totalAssetValueUsd, setTotalAssetValueUsd] = useState(0)
   const [totalCashDeposit, setTotalCashDeposit] = useState(0)
   const [exporting, setExporting] = useState(false)


   const HEAD_COLS: HeadCol[] = [
       { id: '#', label: '#' },
       { id: 'currency', label: 'Currency', sortKey: 'currency' },
       { id: 'provider', label: 'Provider' },
       { id: 'type', label: 'Type' },
       { id: 'balance', label: 'Balance', sortKey: 'balance' },
       { id: 'available', label: 'Available', sortKey: 'available' },
       { id: 'held', label: 'Held', sortKey: 'held' },
       { id: 'value', label: 'Value (USD)' },
       { id: 'wallets', label: 'Wallets' },
       ...(holdingsFilter === 'with_balance' ? [{ id: 'action', label: 'Action' }] : []),
   ]


   useEffect(() => {
       const t = setTimeout(() => setDebouncedSearch(search), 500)
       return () => clearTimeout(t)
   }, [search])


   const buildParams = useCallback((forExport = false) => {
       const params = new URLSearchParams()
       if (!forExport) {
           params.append('page', String(page + 1))
           params.append('limit', String(rowsPerPage))
       }
       if (debouncedSearch) params.append('search', debouncedSearch)
       params.append('sortBy', sortKey)
       params.append('sortOrder', sortDir)
       params.append('provider', provider)
       params.append('holdingsFilter', holdingsFilter)
       return params
   }, [page, rowsPerPage, debouncedSearch, sortKey, sortDir, provider, holdingsFilter])


   const fetchPlatformBalances = useCallback(async () => {
       try {
           const params = buildParams()
           const res = await ApiGetPlatformBalances(`?${params.toString()}`)
           if (res?.status === 'success') {
               const mapped: PlatformAsset[] = res.data.map((item: any) => ({
                   currency: item.currency,
                   currencyName: item.currencyName ?? '',
                   provider: item.provider,
                   currencyType: item.currencyType,
                   balance: item.balance ?? 0,
                   available: item.available ?? 0,
                   held: item.held ?? 0,
                   valueUsd: item.valueUsd ?? 0,
                   wallets: item.wallets ?? null,
               }))
               setAssets(mapped)
               setTotal(res.meta.pagination.total)
               setTotalAssetValueUsd(res.meta.summary?.totalAssetValueUsd ?? 0)
               setTotalCashDeposit(res.meta.summary?.totalCashDeposit ?? 0)
           }
       } catch (err) {
           console.error('Error fetching platform balances:', err)
       }
   }, [buildParams])


   useEffect(() => { fetchPlatformBalances() }, [fetchPlatformBalances])


   const handleSort = (key: SortKey) => {
       if (sortKey === key) {
           setSortDir(d => d === 'asc' ? 'desc' : 'asc')
       } else {
           setSortKey(key)
           setSortDir('asc')
       }
       setPage(0)
   }


   const handleProviderChange = (value: ProviderFilter) => {
       setProvider(value)
       setPage(0)
   }


   const handleHoldingsFilterChange = (value: HoldingsFilter | null) => {
       if (!value) return
       setHoldingsFilter(value)
       setPage(0)
   }


   const handleAssetClick = (asset: PlatformAsset) => {
       navigate(`/admin/platform-balance/${asset.currency}`, { state: { asset } })
   }


   const handleExport = async () => {
       try {
           setExporting(true)
           const params = buildParams(true)
           const blob = await ApiExportPlatformBalances(`?${params.toString()}`)
           const url = window.URL.createObjectURL(blob)
           const link = document.createElement('a')
           link.href = url
           link.download = `platform-balance-${new Date().toISOString().slice(0, 10)}.xlsx`
           document.body.appendChild(link)
           link.click()
           link.remove()
           window.URL.revokeObjectURL(url)
       } catch (err) {
           console.error('Error exporting platform balances:', err)
       } finally {
           setExporting(false)
       }
   }


   return (
       <div className='common-layout flex-column'>
           <Box className="border-10 p-2 px-4 mixed-orange mb-4 both-circle d-flex align-items-center justify-content-between">
               <Typography className='fs-24 text-white fw-medium'>Platform Balance</Typography>
               <Box sx={{ textAlign: 'right' }}>
                   <Typography fontSize={11} color="rgba(255,255,255,0.6)" textTransform="uppercase" letterSpacing="0.08em">Assets Tracked</Typography>
                   <Typography className='fs-20 text-white fw-medium'>{total}</Typography>
               </Box>
           </Box>


           <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
               <Box sx={{ flex: 1, minWidth: 220 }}>
                   <SummaryStatCard label="Total Asset Value" value={fmtUsd(totalAssetValueUsd)} sub="Across all held assets" color="#ff6a3d" icon={<AttachMoneyIcon fontSize="small" />} />
               </Box>
               <Box sx={{ flex: 1, minWidth: 220 }}>
                   <SummaryStatCard label="Total Cash Value" value={fmtUsd(totalCashDeposit)} sub="Held at exchange/broker" color="#22d3ee" icon={<AccountBalanceIcon fontSize="small" />} />
               </Box>
           </Box>


           <Box className="bg-dark-color border-10 p-0" sx={{ overflow: 'hidden' }}>


               <Box sx={{ p: '16px 20px', borderBottom: '1px solid #1e1e1e', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
                   <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                       <Tabs
                           value={provider}
                           onChange={(_, v) => handleProviderChange(v)}
                           sx={{
                               minHeight: 40,
                               background: '#1e1e1e',
                               borderRadius: '10px',
                               p: 0.5,
                               border: '1px solid #333',
                               '& .MuiTabs-indicator': { display: 'none' },
                               '& .MuiTab-root': {
                                   minHeight: 36, borderRadius: '8px', fontSize: 13, fontWeight: 600,
                                   color: '#6f6f6f', textTransform: 'none', px: 2.5, py: 0.5,
                                   transition: 'all 0.2s',
                               },
                               '& .Mui-selected': {
                                   color: '#fff !important',
                                   background: 'linear-gradient(135deg, #f2674a, #d94f35)',
                                   boxShadow: '0 2px 8px rgba(242,103,74,0.4)',
                               },
                           }}
                       >
                           <Tab value="all" label={
                               <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                   <AllInclusiveIcon sx={{ fontSize: 15 }} />
                                   All
                               </Box>
                           } />
                           <Tab value="sfox" label={
                               <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                   <SyncAltIcon sx={{ fontSize: 15 }} />
                                   SFOX
                               </Box>
                           } />
                           <Tab value="c2c" label={
                               <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                   <AccountBalanceIcon sx={{ fontSize: 15 }} />
                                   C2C
                               </Box>
                           } />
                       </Tabs>


                       <ToggleButtonGroup
                           value={holdingsFilter}
                           exclusive
                           onChange={(_, v) => handleHoldingsFilterChange(v)}
                           sx={{
                               minHeight: 40,
                               background: '#1e1e1e',
                               borderRadius: '10px',
                               p: 0.5,
                               border: '1px solid #333',
                               gap: 0.5,
                               '& .MuiToggleButtonGroup-grouped': {
                                   border: 'none !important',
                                   borderRadius: '8px !important',
                               },
                               '& .MuiToggleButton-root': {
                                   minHeight: 36, fontSize: 13, fontWeight: 600,
                                   color: '#6f6f6f', textTransform: 'none', px: 2.5, py: 0.5,
                                   transition: 'all 0.2s',
                               },
                               '& .Mui-selected': {
                                   color: '#fff !important',
                                   background: 'linear-gradient(135deg, #3b82f6, #2563eb) !important',
                                   boxShadow: '0 2px 8px rgba(59,130,246,0.4)',
                               },
                           }}
                       >
                           <ToggleButton value="all">All Assets</ToggleButton>
                           <ToggleButton value="with_balance">Assets With Balance</ToggleButton>
                       </ToggleButtonGroup>
                   </Box>


                   <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                       <TextField
                           placeholder="Search by currency or name..."
                           value={search}
                           onChange={e => { setSearch(e.target.value); setPage(0) }}
                           size="small"
                           sx={searchStyle}
                           InputProps={{
                               startAdornment: (
                                   <InputAdornment position="start">
                                       <SearchIcon className='color-primary' />
                                   </InputAdornment>
                               ),
                           }}
                       />


                       <Button
                           variant="contained"
                           size="small"
                           disabled={exporting}
                           onClick={handleExport}
                           startIcon={<FileDownloadIcon sx={{ fontSize: 16 }} />}
                           sx={{
                               fontSize: 11, fontWeight: 700, borderRadius: '7px',
                               color: '#ff6a3d', border: '1px solid #ff6a3d40',
                               background: '#ff6a3d10', px: 1.5, py: 0.5, minWidth: 80,
                               textTransform: 'none',
                               '&:hover': { background: '#ff6a3d20', borderColor: '#ff6a3d' },
                           }}
                       >
                           Export
                       </Button>
                   </Box>
               </Box>


               <TableContainer sx={{ maxHeight: '60vh' }}>
                   <Table stickyHeader sx={{ '& .MuiTableCell-root': { borderBottom: '1px solid #1e1e1e' } }}>
                       <TableHead>
                           <TableRow>
                               {HEAD_COLS.map(col => (
                                   <TableCell
                                       key={col.id}
                                       sortDirection={col.sortKey && sortKey === col.sortKey ? sortDir : false}
                                       sx={thStyle}
                                   >
                                       {col.sortKey ? (
                                           <TableSortLabel
                                               active={sortKey === col.sortKey}
                                               direction={sortKey === col.sortKey ? sortDir : 'asc'}
                                               onClick={() => handleSort(col.sortKey!)}
                                               sx={{
                                                   color: '#ff6a3d !important',
                                                   '& .MuiTableSortLabel-icon': {
                                                       color: '#ff6a3d !important',
                                                       opacity: sortKey === col.sortKey ? 1 : 0.3,
                                                   },
                                                   '&:hover': { color: '#ff8c66 !important' },
                                                   '&.Mui-active': { color: '#ff6a3d !important' },
                                               }}
                                           >
                                               {col.label}
                                           </TableSortLabel>
                                       ) : (
                                           col.label
                                       )}
                                   </TableCell>
                               ))}
                           </TableRow>
                       </TableHead>
                       <TableBody>
                           {assets.length > 0 ? assets.map((asset, i) => {
                               const m = getMeta(asset.currency)
                               const hasWallets = asset.wallets !== null
                               const activeWallets = hasWallets
                                   ? Object.entries(asset.wallets!).filter(([, v]) => v > 0)
                                   : []


                               return (
                                   <TableRow
                                       key={`${asset.currency}-${asset.provider}-${i}`}
                                       sx={{
                                           transition: 'background 0.15s',
                                           '&:hover': { background: `${m.color}08` },
                                           '& .MuiTableCell-root': { borderBottom: '1px solid #1e1e1e' },
                                       }}
                                   >
                                       <TableCell sx={tdStyle}>
                                           {page * rowsPerPage + i + 1}
                                       </TableCell>


                                       <TableCell sx={tdStyle}>
                                           <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                               <CurrencyBadge currency={asset.currency} />
                                               <Typography fontSize={12} color="#6f6f6f">{m.label}</Typography>
                                           </Box>
                                       </TableCell>


                                       <TableCell sx={tdStyle}>
                                           <Box sx={{
                                               display: 'inline-block', px: 1.5, py: 0.4, borderRadius: '5px',
                                               fontSize: '10px', fontWeight: 600, letterSpacing: '0.05em',
                                               color: asset.provider === 'SFOX' ? '#3b82f6' : '#10b981',
                                               backgroundColor: asset.provider === 'SFOX' ? '#3b82f610' : '#10b98110',
                                               border: `1px solid ${asset.provider === 'SFOX' ? '#3b82f640' : '#10b98140'}`,
                                           }}>
                                               {asset.provider}
                                           </Box>
                                       </TableCell>


                                       <TableCell sx={tdStyle}>
                                           <Box sx={{
                                               display: 'inline-block', px: 1.5, py: 0.4, borderRadius: '5px',
                                               fontSize: '10px', fontWeight: 500, letterSpacing: '0.04em',
                                               color: '#9a9a9a', backgroundColor: '#2a2a2a', border: '1px solid #3f3f3f',
                                           }}>
                                               {asset.currencyType}
                                           </Box>
                                       </TableCell>


                                       <TableCell sx={tdStyle}>
                                           <Typography fontSize={13} fontWeight={700} color={asset.balance < 0 ? '#ef4444' : '#e0e0e0'}>
                                               {fmt(asset.balance, 6)}
                                           </Typography>
                                       </TableCell>


                                       <TableCell sx={tdStyle}>
                                           <Typography fontSize={13} color={asset.available < 0 ? '#ef4444' : '#10b981'}>
                                               {fmt(asset.available, 6)}
                                           </Typography>
                                       </TableCell>


                                       <TableCell sx={tdStyle}>
                                           <Typography fontSize={13} color={asset.held > 0 ? '#f59e0b' : '#5a5a5a'}>
                                               {fmt(asset.held, 6)}
                                           </Typography>
                                       </TableCell>


                                       <TableCell sx={tdStyle}>
                                           <Typography fontSize={13} color="#e0e0e0">
                                               {fmtUsd(asset.valueUsd ?? 0)}
                                           </Typography>
                                       </TableCell>


                                       <TableCell sx={tdStyle}>
                                           {hasWallets && activeWallets.length > 0 ? (
                                               <Tooltip
                                                   title={
                                                       <Box sx={{ fontSize: 11, lineHeight: 1.8 }}>
                                                           {asset.wallets && Object.entries(asset.wallets!).map(([k, v]) => (
                                                               <Box key={k} sx={{ display: 'flex', justifyContent: 'space-between', gap: 3 }}>
                                                                   <span style={{ color: '#9a9a9a', textTransform: 'capitalize' }}>{k}</span>
                                                                   <span style={{ color: v > 0 ? '#e0e0e0' : '#4a4a4a' }}>{fmt(v, 6)}</span>
                                                               </Box>
                                                           ))}
                                                       </Box>
                                                   }
                                                   arrow
                                                   placement="left"
                                               >
                                                   <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, cursor: 'default' }}>
                                                       {activeWallets.slice(0, 2).map(([k, v]) => (
                                                           <Box key={k} sx={{
                                                               px: 1, py: 0.3, borderRadius: '4px',
                                                               fontSize: 10, fontWeight: 500, textTransform: 'capitalize',
                                                               color: '#9a9a9a', backgroundColor: '#2a2a2a', border: '1px solid #3f3f3f',
                                                           }}>
                                                               {k}: {fmt(v, 4)}
                                                           </Box>
                                                       ))}
                                                       {activeWallets.length > 2 && (
                                                           <Typography fontSize={10} color="#5a5a5a">+{activeWallets.length - 2}</Typography>
                                                       )}
                                                   </Box>
                                               </Tooltip>
                                           ) : (
                                               <Typography fontSize={11} color="#3a3a3a">—</Typography>
                                           )}
                                       </TableCell>


                                       {holdingsFilter === 'with_balance' && (
                                           <TableCell sx={tdStyle}>
                                               <Button
                                                   size="small"
                                                   variant="contained"
                                                   startIcon={<VisibilityIcon sx={{ fontSize: 14 }} />}
                                                   onClick={() => handleAssetClick(asset)}
                                                   sx={{
                                                       fontSize: 11, fontWeight: 700, borderRadius: '7px',
                                                       color: '#ff6a3d', border: '1px solid #ff6a3d40',
                                                       background: '#ff6a3d10', px: 1.5, py: 0.5, minWidth: 80,
                                                       textTransform: 'none',
                                                       '&:hover': { background: '#ff6a3d20', borderColor: '#ff6a3d' },
                                                   }}
                                               >
                                                   View
                                               </Button>
                                           </TableCell>
                                       )}
                                   </TableRow>
                               )
                           }) : (
                               <TableRow>
                                   <TableCell colSpan={HEAD_COLS.length} align="center" sx={{ py: 8, background: '#1a1a1a', border: 'none' }}>
                                       <AccountBalanceWalletIcon sx={{ color: '#2f2f2f', fontSize: 40, mb: 1 }} />
                                       <Typography color="#4a4a4a" fontSize={13}>
                                           {search ? 'No assets match your search' : 'No platform balance data found'}
                                       </Typography>
                                   </TableCell>
                               </TableRow>
                           )}
                       </TableBody>
                   </Table>
               </TableContainer>


               <TablePagination
                   component="div"
                   count={total}
                   page={page}
                   onPageChange={(_, p) => setPage(p)}
                   rowsPerPage={rowsPerPage}
                   onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0) }}
                   rowsPerPageOptions={[5, 10, 25, 50]}
                   showFirstButton
                   showLastButton
                   sx={paginationStyle}
               />
           </Box>
       </div>
   )
}


export default PlatformBalance




const tdStyle = {
   color: '#c0c0c0',
   py: 1.4,
   fontSize: 13,
   whiteSpace: 'nowrap' as const,
}


const thStyle = {
   backgroundColor: '#241815',
   color: '#ff6a3d',
   fontSize: 11,
   fontWeight: 700,
   letterSpacing: '0.08em',
   textTransform: 'uppercase' as const,
   whiteSpace: 'nowrap' as const,
   py: 1.5,
   borderBottom: '1px solid #2a2a2a !important',
   position: 'sticky' as const,
   top: 0,
   zIndex: 2,
}


const paginationStyle = {
   color: '#6a6a6a',
   borderTop: '1px solid #252525',
   '& .MuiTablePagination-toolbar': { justifyContent: 'center' },
   '& .MuiTablePagination-selectLabel': { color: '#6a6a6a', margin: 0 },
   '& .MuiTablePagination-displayedRows': { color: '#6a6a6a', margin: 0 },
   '& .MuiSelect-select': { color: '#6a6a6a' },
   '& .MuiSelect-icon': { color: '#6a6a6a' },
   '& .MuiIconButton-root': { color: '#6a6a6a' },
   '& .MuiIconButton-root.Mui-disabled': { color: '#333' },
}


const searchStyle = {
   width: 320,
   background: 'transparent',
   borderRadius: '7px',
   border: '1px solid #898989',
   transition: 'border-color 0.3s',
   '&:hover': { border: '1px solid #ff6a3d' },
   '&:focus-within': { border: '1px solid #ff6a3d' },
   '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
   '& input': { color: '#fff', fontSize: 13 },
   '& input::placeholder': { color: '#555', opacity: 1 },
}
